import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import type { DateRangePickerValue } from '@this/shared/ui/inputs/datepicker/date_range_picker';
import { DateRangePicker } from '@this/shared/ui/inputs/datepicker/date_range_picker';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Table, TableHead, TableRow, TableCell, TableBody } from '@this/shared/ui/data_displays/table';
import { Fetcher, getHttpErrorMessages } from '@this/src/util';

type ExcludedRouteResponseArgs = {
  id: number;
  from_airport: string | null;
  to_airport: string | null;
  target_date: string | null;
  source: string;
  excluded_type: string;
  disabled: boolean;
  created_at: string;
};

type ExcludedRouteListResponse = {
  excluded_routes: ExcludedRouteResponseArgs[];
};

type ExcludedRouteCreateResponse = {
  excluded_routes: ExcludedRouteResponseArgs[];
  skipped_count: number;
};

type ExcludedRouteCreateArgs = {
  excludedRoutes: ExcludedRouteArgs[];
  skippedCount: number;
};

type ExcludedRouteArgs = {
  id: number;
  fromAirport: string | null;
  toAirport: string | null;
  targetDate: string | null;
  source: string;
  excludedType: string;
  disabled: boolean;
  createdAt: string;
};

const convertExcludedRouteResponseToArgs = (response: ExcludedRouteResponseArgs): ExcludedRouteArgs => ({
  id: response.id,
  fromAirport: response.from_airport,
  toAirport: response.to_airport,
  targetDate: response.target_date,
  source: response.source,
  excludedType: response.excluded_type,
  disabled: response.disabled,
  createdAt: response.created_at
});

const convertExcludedRouteCreateResponseToArgs = (
  response: ExcludedRouteCreateResponse
): ExcludedRouteCreateArgs => ({
  excludedRoutes: response.excluded_routes.map(convertExcludedRouteResponseToArgs),
  skippedCount: response.skipped_count
});

const ALL_AIRPORTS_LABEL = 'すべて';

const EXCLUDED_TYPE_LABELS: Record<string, string> = {
  invalid_airport_to: '到着地の空港が不正',
  invalid_airport_from: '出発地の空港が不正',
  invalid_airport_both: '出発地・到着地の空港が不正',
  manual: '手動登録',
  no_service: '就航なし',
  no_itinerary_931: '[931] NO ITINERARY FOUND FOR REQUESTED SEGMENT',
  no_fare_itinerary_866: '[866] NO FARE FOUND FOR REQUESTED ITINERARY'
};

const excludedTypeLabel = (excludedType: string): string => EXCLUDED_TYPE_LABELS[excludedType] || excludedType;

const routeLabel = (route: ExcludedRouteArgs): string => {
  const from = route.fromAirport || ALL_AIRPORTS_LABEL;
  const to = route.toAirport || ALL_AIRPORTS_LABEL;
  return route.targetDate ? `${from} → ${to} / ${route.targetDate}` : `${from} → ${to}`;
};

const EMPTY_TARGET_DATES: DateRangePickerValue = [null, null];

const formatDate = (date: Date | null): string => (date ? moment(date).format('YYYY-MM-DD') : '');

const errorMessageOf = (e: unknown, fallback: string): string => {
  const messages = getHttpErrorMessages(e, fallback);
  return Array.isArray(messages) ? messages.join('、') : messages;
};

const ExcludedRouteTable: React.FC = () => {
  const [excludedRoutes, setExcludedRoutes] = useState<ExcludedRouteArgs[]>([]);
  const [newFromAirport, setNewFromAirport] = useState<string>('');
  const [newToAirport, setNewToAirport] = useState<string>('');
  const [newTargetDates, setNewTargetDates] = useState<DateRangePickerValue>(EMPTY_TARGET_DATES);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchExcludedRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Fetcher.get<ExcludedRouteListResponse>('/god/domestic_air_excluded_routes');
      setExcludedRoutes(response.excluded_routes.map(convertExcludedRouteResponseToArgs));
    } catch (e) {
      setErrorMessage(errorMessageOf(e, 'データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExcludedRoutes();
  }, [fetchExcludedRoutes]);

  const handleChangeFromAirport = useCallback((value: string) => {
    const code = value.trim().toUpperCase();
    setNewFromAirport(code);
    if (!code) setNewTargetDates(EMPTY_TARGET_DATES);
  }, []);

  const handleChangeToAirport = useCallback((value: string) => {
    const code = value.trim().toUpperCase();
    setNewToAirport(code);
    if (!code) setNewTargetDates(EMPTY_TARGET_DATES);
  }, []);

  const handleCreate = useCallback(async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      const response = await Fetcher.post<ExcludedRouteCreateResponse>('/god/domestic_air_excluded_routes', {
        from_airport: newFromAirport,
        to_airport: newToAirport,
        target_date_from: formatDate(newTargetDates[0]),
        target_date_to: formatDate(newTargetDates[1])
      });
      setNewFromAirport('');
      setNewToAirport('');
      setNewTargetDates(EMPTY_TARGET_DATES);
      const created = convertExcludedRouteCreateResponseToArgs(response);
      const skippedMessage =
        created.skippedCount > 0 ? `（登録済みの${created.skippedCount}件はスキップしました）` : '';
      setSuccessMessage(`除外航路を${created.excludedRoutes.length}件追加しました${skippedMessage}`);
      await fetchExcludedRoutes();
    } catch (e) {
      setErrorMessage(errorMessageOf(e, '除外航路の追加に失敗しました'));
      setLoading(false);
    }
  }, [newFromAirport, newToAirport, newTargetDates, fetchExcludedRoutes]);

  const handleToggleDisabled = useCallback(
    async (route: ExcludedRouteArgs) => {
      const message = route.disabled
        ? `ID ${route.id}（${routeLabel(route)}）を有効化しますか？\n再びANAキャッシュ取得の対象外になります。`
        : `ID ${route.id}（${routeLabel(
            route
          )}）を無効化しますか？\n除外判定に使われなくなりますが、レコードは残るため再登録は発生しません。`;
      if (!window.confirm(message)) return;

      setErrorMessage(null);
      setSuccessMessage(null);
      setLoading(true);
      try {
        await Fetcher.patch(`/god/domestic_air_excluded_routes/${route.id}/update_disabled`, {
          disabled: !route.disabled
        });
        setSuccessMessage(route.disabled ? '除外航路を有効化しました' : '除外航路を無効化しました');
        await fetchExcludedRoutes();
      } catch (e) {
        setErrorMessage(errorMessageOf(e, '除外航路の更新に失敗しました'));
        setLoading(false);
      }
    },
    [fetchExcludedRoutes]
  );

  const handleDelete = useCallback(
    async (route: ExcludedRouteArgs) => {
      if (!window.confirm(`ID ${route.id}（${routeLabel(route)}）を削除しますか？`)) return;

      setErrorMessage(null);
      setSuccessMessage(null);
      setLoading(true);
      try {
        await Fetcher.delete(`/god/domestic_air_excluded_routes/${route.id}`);
        setSuccessMessage('除外航路を削除しました');
        await fetchExcludedRoutes();
      } catch (e) {
        setErrorMessage(errorMessageOf(e, '除外航路の削除に失敗しました'));
        setLoading(false);
      }
    },
    [fetchExcludedRoutes]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '20px', fontSize: '12px' }}>
      <p style={{ marginBottom: '12px' }}>
        ANAが就航していない空港・航路を登録すると、以降のANAキャッシュ取得の対象外になります。
        出発空港のみを入力すると「その空港発のすべての航路」、到着空港のみを入力すると「その空港着のすべての航路」が対象になります。
      </p>
      <p style={{ marginBottom: '12px' }}>
        出発空港と到着空港の両方を入力した場合のみ対象期間を指定でき、期間内の各日が1件ずつ就航なしとして登録されます（最大90日。終了日が空欄の場合は開始日のみ）。
        対象期間が空欄の場合はすべての日付が対象になります。
      </p>
      <p style={{ marginBottom: '12px' }}>
        誤検知で自動登録された場合は「無効化」してください。除外判定に使われなくなり、再検出されても再登録されません。
        「削除」した場合は再検出時に再登録されます。
      </p>

      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '16px' }}>
          {successMessage}
        </Alert>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="new-from-airport">出発空港</label>
          <Input
            id="new-from-airport"
            size="small"
            inputProps={{ maxLength: 3 }}
            placeholder="空欄=すべて"
            value={newFromAirport}
            onChange={e => handleChangeFromAirport(e.target.value)}
            style={{ width: '140px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="new-to-airport">到着空港</label>
          <Input
            id="new-to-airport"
            size="small"
            inputProps={{ maxLength: 3 }}
            placeholder="空欄=すべて"
            value={newToAirport}
            onChange={e => handleChangeToAirport(e.target.value)}
            style={{ width: '140px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>対象期間</span>
          <DateRangePicker
            value={newTargetDates}
            onChange={value => setNewTargetDates(value ?? EMPTY_TARGET_DATES)}
            min={new Date()}
            placeholder={['開始日（空欄=すべて）', '終了日']}
            allowEmpty={[false, true]}
            size="small"
            disabled={!newFromAirport || !newToAirport}
          />
        </div>
        <Button onClick={handleCreate} disabled={loading || (!newFromAirport && !newToAirport)}>
          追加
        </Button>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell th>ID</TableCell>
              <TableCell th>出発空港</TableCell>
              <TableCell th>到着空港</TableCell>
              <TableCell th>対象日</TableCell>
              <TableCell th>ソース</TableCell>
              <TableCell th>除外種別</TableCell>
              <TableCell th>状態</TableCell>
              <TableCell th>登録日時</TableCell>
              <TableCell th>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {excludedRoutes.map(route => (
              <TableRow key={route.id} style={route.disabled ? { color: '#999' } : undefined}>
                <TableCell>{route.id}</TableCell>
                <TableCell>{route.fromAirport || ALL_AIRPORTS_LABEL}</TableCell>
                <TableCell>{route.toAirport || ALL_AIRPORTS_LABEL}</TableCell>
                <TableCell nowrap>{route.targetDate || ALL_AIRPORTS_LABEL}</TableCell>
                <TableCell>{route.source}</TableCell>
                <TableCell>{excludedTypeLabel(route.excludedType)}</TableCell>
                <TableCell>{route.disabled ? '無効' : '有効'}</TableCell>
                <TableCell nowrap>{moment(route.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                <TableCell nowrap>
                  <Button onClick={() => handleToggleDisabled(route)} style={{ marginRight: '8px' }}>
                    {route.disabled ? '有効化' : '無効化'}
                  </Button>
                  <Button onClick={() => handleDelete(route)}>削除</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ExcludedRouteTable;
